import { reactive } from 'vue'
import { KeepAwake } from '@capacitor-community/keep-awake'
import * as Sentry from '@sentry/vue'

import Memory from '@/lib/memory'
import moveProSyncNative from '@/lib/native/moveproSync'
import {
    acquireMoveProNativeSyncLock,
    getMoveProNativeSyncLock,
    MOVE_PRO_NATIVE_SYNC_ACTIVE_STATES,
    MOVE_PRO_NATIVE_SYNC_HEARTBEAT_MS,
    releaseMoveProNativeSyncLock,
    renewMoveProNativeSyncLock,
} from '@/lib/helpers/movepro-native-sync-lock'
import useSensor from './sensor'
import useFile from './file'

const RUN_MANIFESTS_STORAGE_KEY = 'moveProBluetoothNativeRunManifests'

const readMaybeRef = (value) => value?.value ?? value

const createRunId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }

    return `movepro-bluetooth-native-${Date.now()}`
}

const getFileKey = (file) => file?.getFilename?.() || file?.properties?.filename || file?.fileKey || file?.filename || ''

const buildNativeSyncFilePayload = (file) => ({
    fileKey: file?.getFilename?.() || file?.properties?.filename || '',
    filename: file?.properties?.filename || file?.getFilename?.() || '',
    index: Number(file?.properties?.index ?? -1),
    startPage: Number(file?.properties?.startPage ?? -1),
    numberOfPages: Number(file?.properties?.numberOfPages ?? 0),
    size: Number(file?.properties?.size ?? 0),
    weekNumber: file?.properties?.weekNumber ?? '',
    iTOW: file?.properties?.iTOW ?? '',
    latitude: file?.properties?.latitude ?? '',
    longitude: file?.properties?.longitude ?? '',
    firmware: file?.properties?.firmware ?? '',
    uuid: file?.properties?.uuid || '',
    language: file?.properties?.language || '',
    discipline: file?.properties?.discipline ?? '',
    horseId: file?.properties?.horseId ?? undefined,
    riderId: file?.properties?.riderId ?? undefined,
    goal: file?.properties?.goal ?? '',
    goal_value: file?.properties?.goal_value ?? '',
    forceUpload: file?.properties?.forceUpload === true,
    metadataSource: file?.properties?.metadataSource || '',
})

const loadRunManifests = async () => {
    const manifests = await Memory.get(RUN_MANIFESTS_STORAGE_KEY, { otherwise: {} })
    return manifests && typeof manifests === 'object' && !Array.isArray(manifests) ? manifests : {}
}

const persistRunManifest = async ({ runId, deviceId, files }) => {
    const manifests = await loadRunManifests()
    manifests[runId] = {
        runId,
        deviceId,
        files,
        updatedAt: Date.now(),
    }
    await Memory.set(RUN_MANIFESTS_STORAGE_KEY, manifests)
}

const removeRunManifest = async (runId) => {
    if (!runId) return
    const manifests = await loadRunManifests()
    if (!manifests[runId]) return
    delete manifests[runId]
    await Memory.set(RUN_MANIFESTS_STORAGE_KEY, manifests)
}

export default ({ triggerEvent, language, bluetooth }) => {
    const sensor = useSensor()
    const nativeSync = reactive({
        active: false,
        phase: 'idle',
        runId: null,
        currentFileIndex: -1,
        currentFileProgressPercent: 0,
        totalFiles: 0,
        lastError: null,
    })

    const runFilesByRunId = new Map()
    const completedFileKeys = new Set()
    const uploadsInFlight = new Set()
    let heartbeatId = null
    let refreshInFlight = null

    sensor.language = language
    sensor.driver = 'bluetoothNative'
    sensor.hasWebAPIAvailble = moveProSyncNative.isAvailable()
    sensor.capabilities = {
        memorySync: true,
        recording: false,
        liveStatus: false,
        eraseMemory: false,
    }
    sensor.nativeSync = nativeSync

    const isActiveNativeState = (state) => MOVE_PRO_NATIVE_SYNC_ACTIVE_STATES.has(state)

    const copyBluetoothState = ({ copyFiles = false } = {}) => {
        Object.assign(sensor.properties, bluetooth.properties)
        sensor.isEnabled.value = !!readMaybeRef(bluetooth.isEnabled)
        sensor.isConnected.value = !!readMaybeRef(bluetooth.isConnected)
        sensor.isNearby.value = !!readMaybeRef(bluetooth.isNearby)

        if (copyFiles) {
            sensor.files.splice(0, sensor.files.length, ...bluetooth.files)
            sensor.properties.filesCount = sensor.files.length
        }
    }

    const showUnavailable = async () => {
        triggerEvent('onNativeFeatureUnavailable')
        return false
    }

    const findManifestFile = async (runId, completedFile = {}) => {
        const manifests = await loadRunManifests()
        const manifestFiles = manifests?.[runId]?.files || []
        const completedKey = completedFile.fileKey || completedFile.filename || ''
        const completedIndex = Number(completedFile.index)

        return manifestFiles.find((file) => {
            const fileKey = file.fileKey || file.filename || ''
            const fileIndex = Number(file.index)

            return (
                (!!completedKey && completedKey === fileKey) ||
                (Number.isFinite(completedIndex) && Number.isFinite(fileIndex) && completedIndex === fileIndex)
            )
        })
    }

    const findNativeSyncFile = async (runId, completedFile = {}) => {
        const completedKey = completedFile.fileKey || completedFile.filename || ''
        const completedIndex = Number(completedFile.index)
        const runFiles = runFilesByRunId.get(runId) || []
        let file = runFiles.find((candidate) => {
            const candidateKey = getFileKey(candidate)
            const candidateIndex = Number(candidate?.properties?.index)

            return (
                (!!completedKey && completedKey === candidateKey) ||
                (Number.isFinite(completedIndex) && Number.isFinite(candidateIndex) && completedIndex === candidateIndex)
            )
        })

        if (file) return file

        file = sensor.files.find((candidate) => {
            const candidateKey = getFileKey(candidate)
            const candidateIndex = Number(candidate?.properties?.index)

            return (
                (!!completedKey && completedKey === candidateKey) ||
                (Number.isFinite(completedIndex) && Number.isFinite(candidateIndex) && completedIndex === candidateIndex)
            )
        })

        if (file) return file

        const manifestFile = await findManifestFile(runId, completedFile)
        if (!manifestFile) return null

        file = await useFile({
            ...manifestFile,
            ...completedFile,
            selected: true,
            syncedPercent: Number(completedFile.syncedPercent) || 100,
            uploadedPercent: 0,
            uploaded: false,
        })
        sensor.files.push(file)
        sensor.properties.filesCount = sensor.files.length
        return file
    }

    const uploadCompletedFile = async (runId, completedFile) => {
        const file = await findNativeSyncFile(runId, completedFile)
        const fileKey = getFileKey(file) || completedFile?.fileKey || completedFile?.filename || ''

        if (!file || !fileKey) {
            return false
        }

        if (uploadsInFlight.has(fileKey)) {
            return true
        }

        uploadsInFlight.add(fileKey)
        completedFileKeys.add(`${runId}:${fileKey}`)

        try {
            await file.save({
                syncedPercent: 100,
                uploadedPercent: 0,
                uploaded: false,
                uploading: false,
                selected: true,
            })
            await file.upload()
        } catch (error) {
            const sourceMissing =
                error?.code === 'sensor-file-upload-source-missing' ||
                error?.message === 'sensor-file-upload-source-missing'

            await file
                .markAsUploadFailed?.({
                    uploadedPercent: 0,
                })
                .catch(() => {})

            if (sourceMissing) {
                await file
                    .save?.({
                        syncedPercent: 0,
                        uploadedPercent: 0,
                        uploaded: false,
                        uploading: false,
                        selected: true,
                    })
                    .catch(() => {})
            }

            triggerEvent('onUploadFailed', file, error)

            try {
                Sentry.captureException(error)
            } catch {}
        } finally {
            uploadsInFlight.delete(fileKey)
        }

        return true
    }

    const drainCompletedFiles = async (runId) => {
        if (!runId) return

        const result = await moveProSyncNative.drainCompletedFiles()
        const completedFiles = Array.isArray(result?.files) ? result.files : []

        for (const completedFile of completedFiles) {
            const completedKey = completedFile?.fileKey || completedFile?.filename || ''
            const processedKey = `${runId}:${completedKey || completedFile?.index || completedFiles.indexOf(completedFile)}`

            if (completedFileKeys.has(processedKey)) {
                continue
            }

            const processed = await uploadCompletedFile(runId, completedFile)
            if (processed) {
                completedFileKeys.add(processedKey)
            }
        }
    }

    const markCurrentFileSyncing = (runId, currentFileIndex, progressPercent = 1) => {
        const index = Number(currentFileIndex)
        if (!Number.isFinite(index) || index < 0) return

        const files = runFilesByRunId.get(runId) || sensor.files
        const file = files[index]
        const nextProgress = Math.max(1, Math.min(99, Math.round(Number(progressPercent) || 1)))

        if (file?.properties && Number(file.properties.syncedPercent) < nextProgress) {
            file.properties.syncedPercent = nextProgress
        }
    }

    const stopHeartbeat = () => {
        if (heartbeatId) {
            clearInterval(heartbeatId)
            heartbeatId = null
        }
    }

    const releaseNativeSync = async (runId = null, { removeManifest = true } = {}) => {
        stopHeartbeat()
        await releaseMoveProNativeSyncLock({ runId })
        if (removeManifest && runId) {
            await removeRunManifest(runId)
        }
        runFilesByRunId.delete(runId)
        nativeSync.active = false
        nativeSync.phase = 'idle'
        nativeSync.runId = null
        nativeSync.currentFileIndex = -1
        nativeSync.currentFileProgressPercent = 0
        nativeSync.totalFiles = 0
        sensor.isBusy.value = false
        await KeepAwake.allowSleep().catch(() => {})
    }

    const refreshNativeSyncState = async ({ runId = null, deviceId = null } = {}) => {
        if (refreshInFlight) return refreshInFlight

        refreshInFlight = (async () => {
            try {
                const state = await moveProSyncNative.getState()
                const stateName = state?.state
                const nextRunId = state?.runId || runId

                nativeSync.runId = nextRunId
                nativeSync.phase = stateName || 'idle'
                nativeSync.currentFileIndex = Number(state?.currentFileIndex ?? -1)
                nativeSync.currentFileProgressPercent = Number(state?.currentFileProgressPercent ?? 0)
                nativeSync.lastError = state?.lastError || null

                if (isActiveNativeState(stateName)) {
                    await renewMoveProNativeSyncLock({
                        runId: nextRunId,
                        deviceId,
                    })
                    nativeSync.active = true
                    sensor.isBusy.value = true
                    sensor.isConnected.value = true
                    markCurrentFileSyncing(nextRunId, state?.currentFileIndex, state?.currentFileProgressPercent)
                    await drainCompletedFiles(nextRunId)
                    return state
                }

                if (stateName === 'finished') {
                    await drainCompletedFiles(nextRunId)
                    await releaseNativeSync(nextRunId)
                    return state
                }

                if (stateName === 'interrupted') {
                    await releaseNativeSync(nextRunId, { removeManifest: false })
                    return state
                }

                await releaseNativeSync(nextRunId)
                return state
            } catch (error) {
                await releaseNativeSync(runId, { removeManifest: false })
                return null
            }
        })().finally(() => {
            refreshInFlight = null
        })

        return refreshInFlight
    }

    const startHeartbeat = ({ runId, deviceId }) => {
        stopHeartbeat()
        heartbeatId = setInterval(() => {
            refreshNativeSyncState({ runId, deviceId })
        }, MOVE_PRO_NATIVE_SYNC_HEARTBEAT_MS)
    }

    const restoreRunFiles = async (runId) => {
        const manifests = await loadRunManifests()
        const files = manifests?.[runId]?.files || []
        if (!Array.isArray(files) || files.length === 0) return []

        const restoredFiles = []
        for (const payload of files) {
            if (!payload?.uuid) continue
            const file = await useFile({
                ...payload,
                selected: true,
            })
            restoredFiles.push(file)
        }

        sensor.files.splice(0, sensor.files.length, ...restoredFiles)
        sensor.properties.filesCount = sensor.files.length
        runFilesByRunId.set(runId, restoredFiles)
        nativeSync.totalFiles = restoredFiles.length
        return restoredFiles
    }

    const resumeExistingNativeSync = async () => {
        const lock = await getMoveProNativeSyncLock()
        if (!lock) return

        await restoreRunFiles(lock.runId)
        sensor.properties.deviceId = lock.deviceId || sensor.properties.deviceId
        sensor.isConnected.value = true
        sensor.isBusy.value = true
        nativeSync.active = true
        nativeSync.runId = lock.runId
        startHeartbeat({
            runId: lock.runId,
            deviceId: lock.deviceId,
        })
        await refreshNativeSyncState({
            runId: lock.runId,
            deviceId: lock.deviceId,
        })
    }

    sensor.connect = async () => {
        if (!moveProSyncNative.isAvailable()) {
            return showUnavailable()
        }

        if (sensor.isBusy.value || (await getMoveProNativeSyncLock())) {
            triggerEvent('onProcessLocked')
            return false
        }

        sensor.isBusy.value = true
        try {
            await bluetooth.connect()
            copyBluetoothState()
            return sensor.isConnected.value
        } finally {
            sensor.isBusy.value = false
        }
    }

    sensor.disconnect = async (forgetDevice = false) => {
        const lock = await getMoveProNativeSyncLock()
        if (lock) {
            await moveProSyncNative.stopSync().catch(() => {})
            await releaseNativeSync(lock.runId, { removeManifest: false })
        }

        await bluetooth.disconnect?.(forgetDevice).catch(() => {})
        sensor.resetSensor()
        nativeSync.active = false
        nativeSync.phase = 'idle'
    }

    sensor.getSessionsList = async (force = false) => {
        if (!sensor.isConnected.value) return false
        if (!readMaybeRef(bluetooth.isConnected)) {
            return showUnavailable()
        }
        if (sensor.isBusy.value) {
            triggerEvent('onProcessLocked')
            return false
        }

        sensor.isBusy.value = true
        sensor.markSessionListLoading?.()
        try {
            await bluetooth.getSessionsList(force)
            copyBluetoothState({ copyFiles: true })
            sensor.markSessionListLoaded?.()
            return true
        } catch (error) {
            sensor.markSessionListFailed?.()
            throw error
        } finally {
            sensor.isBusy.value = false
        }
    }

    sensor.syncSelectedSessions = async () => {
        if (!moveProSyncNative.isAvailable()) {
            return showUnavailable()
        }

        if (sensor.isBusy.value || (await getMoveProNativeSyncLock())) {
            triggerEvent('onProcessLocked')
            return false
        }

        const selectedFiles = sensor.files.filter((file) => file.properties.selected === true)
        if (selectedFiles.length === 0) return false

        const runId = createRunId()
        const deviceId = sensor.properties.deviceId || bluetooth.properties?.deviceId || null
        const files = selectedFiles.map(buildNativeSyncFilePayload)

        if (!deviceId) {
            triggerEvent('onNativeSyncMissingDevice')
            return false
        }

        sensor.isBusy.value = true
        nativeSync.active = true
        nativeSync.phase = 'prepared'
        nativeSync.runId = runId
        nativeSync.totalFiles = selectedFiles.length
        nativeSync.currentFileIndex = -1
        nativeSync.currentFileProgressPercent = 0
        nativeSync.lastError = null
        runFilesByRunId.set(runId, selectedFiles)
        await persistRunManifest({ runId, deviceId, files })
        await acquireMoveProNativeSyncLock({ runId, deviceId })
        await KeepAwake.keepAwake().catch(() => {})

        try {
            await bluetooth.prepareForNativeSync?.()

            const result = await moveProSyncNative.startSync({
                runId,
                deviceId,
                files,
            })

            if (isActiveNativeState(result?.state)) {
                startHeartbeat({ runId, deviceId })
            } else if (result?.state === 'finished') {
                await refreshNativeSyncState({ runId, deviceId })
            } else {
                await releaseNativeSync(runId, { removeManifest: false })
            }

            return true
        } catch (error) {
            await releaseNativeSync(runId, { removeManifest: false })
            try {
                Sentry.captureException(error)
            } catch {}
            triggerEvent('onNativeSyncFailed', error)
            return false
        }
    }

    sensor.askPermissions = (...args) => bluetooth.askPermissions?.(...args)
    sensor.eraseMemory = showUnavailable
    sensor.idle = showUnavailable
    sensor.record = showUnavailable
    sensor.pause = showUnavailable
    sensor.resume = showUnavailable
    sensor.stop = showUnavailable
    sensor.powerOn = showUnavailable
    sensor.shutdown = showUnavailable
    sensor.setOTAMode = showUnavailable
    sensor.setInitMode = showUnavailable
    sensor.setADVMode = showUnavailable
    sensor.setNavigationMode = showUnavailable
    sensor.setMemoryMode = showUnavailable
    sensor.setCalibrationMode = showUnavailable
    sensor.resetSettings = showUnavailable
    sensor.setLostMode = showUnavailable
    sensor.setSTMBLMode = showUnavailable
    sensor.setSTMNBLMode = showUnavailable
    sensor.updateFirmware = showUnavailable
    sensor.updateFirmwareNRF = showUnavailable
    sensor.updateFirmwareSTM = showUnavailable
    sensor.getMode = showUnavailable

    resumeExistingNativeSync().catch((error) => {
        try {
            Sentry.captureException(error)
        } catch {}
    })

    return sensor
}
